import { useCallback } from 'react';
import { useCsvEditorContext } from '../contexts/CsvEditorContext';
import { useRowFiltering } from './useRowFiltering';
import { useRowNavigation } from './useRowNavigation';
import { useCsvSave } from './useCsvSave';
import { useGridLayout } from './useGridLayout';
import { useRowState } from './useRowState';
import { isRowMissing, isRowChanged } from '../utils/rowHelpers';

/**
 * Master hook that composes all CsvEditor sub-hooks.
 * Provides a unified API for the CsvEditor component.
 * 
 * This hook assumes CsvEditorContext is already provided by a parent component.
 * 
 * @param {string} uploadCsvString - Function to upload CSV string
 * @param {Function} syncBqToPgNames - Function to sync BigQuery to Postgres names
 * @param {number} pageSize - Page size for pagination
 * @param {Array|null} finalFilteredRows - Final filtered rows from DataGrid (includes multi-filters). If null, uses filteredRows from useRowFiltering.
 */
export const useCsvEditor = (uploadCsvString, syncBqToPgNames, pageSize = 50, finalFilteredRows = null) => {
  const {
    rows,
    isLoading,
    updateCell,
    originalRowsMap
  } = useCsvEditorContext();

  // Layout hook
  const { isFiltersFixed, gridRightEdge, gridContainerRef } = useGridLayout(
    isLoading,
    rows.length
  );

  // Filtering hook
  const {
    showOnlyChanged,
    setShowOnlyChanged,
    filteredRows,
    changedRowsCount
  } = useRowFiltering();

  // Use final filtered rows from DataGrid if available, otherwise use filteredRows from useRowFiltering
  // finalFilteredRows includes both useRowFiltering filters AND CarwizDataGrid multi-filters
  const rowsForNavigation = finalFilteredRows !== null ? finalFilteredRows : filteredRows;

  // Navigation hook - uses final filtered rows that match what's actually displayed in DataGrid
  const {
    selectedRowId,
    page,
    pageSize: currentPageSize,
    handleRowClick,
    handlePageChange,
    handlePageSizeChange,
    handleNextMissing,
    handlePreviousMissing,
    canNavigateNext,
    canNavigatePrevious
  } = useRowNavigation(
    rowsForNavigation,
    pageSize,
    showOnlyChanged,
    gridContainerRef
  );

  // Save hook
  const { isSaving, handleSave } = useCsvSave(uploadCsvString, syncBqToPgNames);

  // Row state hook
  const { getRowState } = useRowState();

  // Handle cell edit commit with save prevention
  const handleCellEditCommit = useCallback(
    (params) => {
      // Prevent editing while saving
      if (isSaving) {
        return;
      }
      updateCell(params.id, params.field, params.value);
    },
    [updateCell, isSaving]
  );

  // Wrapper handlers that prevent interaction during save
  const handleRowClickWrapper = useCallback(
    (params) => {
      if (!isSaving) {
        handleRowClick(params);
      }
    },
    [handleRowClick, isSaving]
  );

  const handlePageChangeWrapper = useCallback(
    (params) => {
      if (!isSaving) {
        handlePageChange(params);
      }
    },
    [handlePageChange, isSaving]
  );

  const handlePageSizeChangeWrapper = useCallback(
    (params) => {
      if (!isSaving) {
        handlePageSizeChange(params);
      }
    },
    [handlePageSizeChange, isSaving]
  );

  // Get row class name for styling rows based on their state
  const getRowClassName = useCallback(
    (params) => {
      const classes = [];
      const rowState = getRowState(params.id);

      // Check if row is currently missing required fields
      if (rowState.isMissing) {
        classes.push('missing-row');
      } else {
        classes.push('complete-row');
      }

      // Check if row has been changed
      if (rowState.isChanged) {
        classes.push('changed-row');
      }

      // Check if row is selected
      if (selectedRowId === params.id) {
        classes.push('selected-row');
      }

      return classes.join(' ');
    },
    [getRowState, selectedRowId]
  );

  return {
    // Data
    rows,
    filteredRows,
    isLoading,
    isSaving,
    
    // Filtering
    showOnlyChanged,
    setShowOnlyChanged,
    changedRowsCount,
    
    // Navigation
    selectedRowId,
    page,
    pageSize: currentPageSize,
    canNavigateNext,
    canNavigatePrevious,
    
    // Layout
    isFiltersFixed,
    gridRightEdge,
    gridContainerRef,
    
    // Actions
    handleCellEditCommit,
    handleRowClick: handleRowClickWrapper,
    handlePageChange: handlePageChangeWrapper,
    handlePageSizeChange: handlePageSizeChangeWrapper,
    handleNextMissing,
    handlePreviousMissing,
    handleSave,
    
    // Utilities
    getRowClassName,
    originalRowsMap
  };
};
